import { STAGES } from './pipeline-core'

const DB_NAME = 'kkm-proxy'
const STORE = 'commands'
const TTL_MS = 7 * 24 * 60 * 60 * 1000

/**
 * IndexedDB-журнал команд прокси. Формат записи:
 * { commandId, backendCommandId, payload, deadlineAt, stage,
 *   claimerToken, sendAttempts, result, updatedAt }
 *
 * Инвариант: стадия пишется ДО внешнего действия (crash-safety):
 * claimed = «Execute гарантированно не вызывался», sent = «возможно вызывался».
 * Журнал - слой восстановления: его отказ (приватный режим, квоты) не должен
 * ломать печать, поэтому все операции деградируют в no-op.
 */
class KkmJournal {
  constructor () {
    this._dbPromise = null
    this._unavailable = typeof indexedDB === 'undefined'
  }

  _db () {
    if (this._unavailable) return Promise.reject(new Error('IndexedDB недоступен'))
    if (!this._dbPromise) {
      this._dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1)
        req.onupgradeneeded = () => {
          req.result.createObjectStore(STORE, { keyPath: 'commandId' })
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => {
          this._unavailable = true
          reject(req.error)
        }
      })
    }
    return this._dbPromise
  }

  async _tx (mode, fn) {
    try {
      const db = await this._db()
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode)
        const request = fn(tx.objectStore(STORE))
        tx.oncomplete = () => resolve(request ? request.result : undefined)
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error)
      })
    } catch (e) {
      console.warn('KkmJournal недоступен:', e)
      return null
    }
  }

  async put (record) {
    return this._tx('readwrite', store => store.put({ ...record, updatedAt: Date.now() }))
  }

  async patch (commandId, fields) {
    const record = await this.get(commandId)
    if (!record) return null
    return this.put({ ...record, ...fields })
  }

  async get (commandId) {
    return this._tx('readonly', store => store.get(commandId))
  }

  async remove (commandId) {
    return this._tx('readwrite', store => store.delete(commandId))
  }

  async all () {
    const result = await this._tx('readonly', store => store.getAll())
    return result || []
  }

  async cleanup () {
    const records = await this.all()
    const expired = records.filter(r => Date.now() - r.updatedAt > TTL_MS)
    for (const record of expired) {
      await this.remove(record.commandId)
    }
  }
}

export { STAGES }
export const kkmJournal = new KkmJournal()
